import React, { forwardRef, useState } from 'react';
import clsx from 'clsx';
import { useMediaQuery } from 'fronton-react';
import { SlicedSuggestions } from 'types/suggestions';
import { suggestionsContainer, suggestionsRoot, suggestionsActive } from './styles';
import { Suggestions } from './suggestions';
import { SuggestionPreloader } from './suggestions-preloader';
import { Error } from './suggestions-error';
import { SuggestionContainerRefProvider } from '../hooks';
import { useVisualViewportApi } from 'common/hooks';

export type SearchbarModalProps = {
  searchQuery: string;
  suggestions: SlicedSuggestions | null;
  onMouseEnter: (idx: number) => void;
  onMouseLeave: () => void;
  fetchSuggestions: () => void;
  suggestionsLoading?: boolean;
  onSelectSuggestion?: () => void;
  logoUrl: string;
  searchHref: string;
  error?: boolean;
  expanded: boolean;
  focusGroup: string;
  collapse: () => void;
  suggestionSelector: string;
  index: number;
  isSrp: boolean;
  notFoundMessage: string | false | undefined;
  newSuggestions: boolean;
};

export const SearchbarModal = forwardRef<HTMLDivElement, SearchbarModalProps>(
  (
    {
      searchQuery,
      suggestions,
      searchHref,
      onMouseEnter,
      fetchSuggestions,
      onMouseLeave,
      expanded,
      error,
      suggestionSelector,
      index,
      isSrp,
      onSelectSuggestion,
      notFoundMessage,
      suggestionsLoading,
      collapse,
      newSuggestions,
    },
    ref,
  ) => {
    const suggestionsArePresent = suggestions && (suggestions[0].length || suggestions[1].length);

    const hideSuggestions = error || (!suggestionsArePresent && notFoundMessage === false);
    const showSuggestions = !hideSuggestions;

    const isMobile = useMediaQuery({
      maxWidth: 767,
    });

    const [safeAreaHeight, setSafeAreaHeight] = useState('100vh');

    useVisualViewportApi({ handle: 'resize' }, (visualView) => {
      setSafeAreaHeight(`${visualView.height}px`);
    });

    const style = isMobile ? { height: safeAreaHeight } : {};

    return (
      <div className={clsx(suggestionsRoot, { [suggestionsActive]: expanded })} role="dialog">
        <div ref={ref} style={style} className={suggestionsContainer} data-qa="suggestions-container">
          <SuggestionContainerRefProvider containerRef={ref}>
            {error && <Error fetchSuggestions={fetchSuggestions} />}
            {suggestionsLoading && <SuggestionPreloader />}
            {showSuggestions && !suggestionsLoading && (
              <Suggestions
                searchQuery={searchQuery}
                onSelectSuggestion={onSelectSuggestion}
                isSrp={isSrp}
                suggestions={suggestions}
                suggestionSelector={suggestionSelector}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                searchHref={searchHref}
                index={index}
                notFoundMessage={notFoundMessage}
                collapse={collapse}
                newSuggestions={newSuggestions}
              />
            )}
          </SuggestionContainerRefProvider>
        </div>
      </div>
    );
  },
);
